import { storeAuthError } from "./auth-error-store"

export type AuthRedirectKind = "easyauth" | "msal"

const WINDOW_MS = 2 * 60 * 1000
const MAX_ATTEMPTS = 3
// Rapid-burst guard: catch the case where MSAL bounces several times in a few seconds
// (e.g. placeholder client_id → AADSTS error → immediate retry). The 2-minute window above
// is too lenient to stop this before the user sees a flapping loop.
//
// Threshold is 3, not 2, because guardAuthLoop fires per redirect trigger (not per completed
// navigation). Two concurrent API calls hitting 401/CORS close together can each trip the
// guard before the first redirect finishes — with threshold 2 that turns a recoverable
// re-login into a hard /auth/error. A real loop produces many more bounces than 2.
const BURST_WINDOW_MS = 5 * 1000
const BURST_MAX_ATTEMPTS = 3
const KEY_PREFIX = "auth_redirect_attempts_v1"

type StoredAttempts = {
  attempts: number[]
}

function getKey(kind: AuthRedirectKind): string {
  return `${KEY_PREFIX}:${kind}`
}

function readAttempts(kind: AuthRedirectKind): number[] {
  try {
    const raw = sessionStorage.getItem(getKey(kind))
    if (!raw) return []
    const parsed = JSON.parse(raw) as Partial<StoredAttempts>
    if (!Array.isArray(parsed.attempts)) return []
    return parsed.attempts.filter((t): t is number => typeof t === "number" && Number.isFinite(t))
  } catch {
    return []
  }
}

function writeAttempts(kind: AuthRedirectKind, attempts: number[]): void {
  try {
    const payload: StoredAttempts = { attempts }
    sessionStorage.setItem(getKey(kind), JSON.stringify(payload))
  } catch {
    // ignore (sessionStorage may be unavailable)
  }
}

/**
 * Records an auth redirect attempt and returns whether we should stop redirecting
 * to avoid infinite loops.
 */
export function recordAuthRedirectAttempt(kind: AuthRedirectKind): {
  blocked: boolean
  attemptCount: number
  windowMs: number
  maxAttempts: number
} {
  const now = Date.now()
  const recent = readAttempts(kind).filter((t) => now - t < WINDOW_MS)
  recent.push(now)
  writeAttempts(kind, recent)

  const burstCount = recent.filter((t) => now - t < BURST_WINDOW_MS).length
  const burstBlocked = burstCount >= BURST_MAX_ATTEMPTS
  const sustainedBlocked = recent.length >= MAX_ATTEMPTS

  return {
    blocked: burstBlocked || sustainedBlocked,
    attemptCount: recent.length,
    windowMs: burstBlocked ? BURST_WINDOW_MS : WINDOW_MS,
    maxAttempts: burstBlocked ? BURST_MAX_ATTEMPTS : MAX_ATTEMPTS,
  }
}

export function clearAuthRedirectAttempts(kind?: AuthRedirectKind): void {
  try {
    if (kind) {
      sessionStorage.removeItem(getKey(kind))
      return
    }
    sessionStorage.removeItem(getKey("easyauth"))
    sessionStorage.removeItem(getKey("msal"))
  } catch {
    // ignore
  }
}

/**
 * Guards against auth redirect loops by recording the attempt and redirecting
 * to the error page if too many attempts have been made.
 *
 * @param kind - The auth provider type ("easyauth" or "msal")
 * @param returnTo - The path to return to after successful auth
 * @returns true if the redirect was blocked (already navigating to error page), false otherwise
 */
export function guardAuthLoop(kind: AuthRedirectKind, returnTo: string): boolean {
  const attempt = recordAuthRedirectAttempt(kind)
  if (!attempt.blocked) return false

  storeAuthError({
    reason: "Too many sign-in attempts detected",
    message: "We detected a possible sign-in loop. Please try again later or contact support.",
    provider: kind,
    returnTo,
    context: attempt,
  })
  window.location.href = `${window.location.origin}/auth/error`
  return true
}
